import { Children, useRef } from "react";
import LocationAutocomplete from "../LocationAutocomplete";
import { VoiceButton } from "../VoiceInput";
import { OnbHelpButton } from "../onboarding/OnbAtoms";
import { useLanguage } from "../../context/LanguageContext";
import { dateLocale, clockText } from "../../utils/leadDisplay";

/* The pieces the two trip forms are built from - the cab trip (pages/LocationPicker)
   and the hire-driver trip (pages/HireLocationPicker). One file, so the two forms a
   customer moves between look and behave as one product: the same header, the same
   cards, the same place boxes, the same date and time chips. */

const svg = (children, size = 16, extra = {}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...extra}>{children}</svg>
);

/* The From and To marks (Oct 2026, mock Fix_A change 2): a filled pin in a soft circle -
   green for where the trip starts, red for where it ends - wherever a place is shown. */
const MARK = { pickup: ["#EAF5D3", "#5E8C00"], drop: ["#FDE2E2", "#E11D48"], stop: ["#F1F5F9", "#94A3B8"] };
const pinPath = (fill) => <><path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" fill={fill} stroke="none" /><circle cx="12" cy="10" r="2.6" fill="#FFFFFF" stroke="none" /></>;
export function PlaceMark({ kind = "pickup", size = 26, plain = false }) {
  const [bg, fg] = MARK[kind] || MARK.pickup;
  if (plain) return <span className="shrink-0 inline-flex">{svg(pinPath(fg), Math.round(size * 0.62))}</span>;
  return (
    <span className="shrink-0 rounded-full inline-flex items-center justify-center" style={{ width: size, height: size, background: bg }} aria-hidden="true">
      {svg(pinPath(fg), Math.round(size * 0.6))}
    </span>
  );
}

export const TripIcons = {
  pin: (s) => svg(<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" /></>, s),
  calendar: (s) => svg(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></>, s),
  clock: (s) => svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>, s),
  sliders: (s) => svg(<><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" /></>, s),
  car: (s) => svg(<><path d="M5 17h14M5 17a2 2 0 0 1-2-2v-3h18v3a2 2 0 0 1-2 2" /><path d="M3 12l2.4-4.9A2 2 0 0 1 7.2 6h9.6a2 2 0 0 1 1.8 1.1L21 12" /><circle cx="7.5" cy="17" r="1.5" /><circle cx="16.5" cy="17" r="1.5" /></>, s),
  users: (s) => svg(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></>, s),
  bag: (s) => svg(<><rect x="4" y="7" width="16" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M9 20v1M15 20v1" /></>, s),
  note: (s) => svg(<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>, s),
  target: (s) => svg(<><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 1v3M12 20v3M1 12h3M20 12h3" /></>, s),
  swap: (s) => svg(<path d="M7 4v16M3 8l4-4 4 4M17 20V4M21 16l-4 4-4-4" />, s),
  arrowRight: (s) => svg(<path d="M5 12h14M13 6l6 6-6 6" />, s),
  arrowLeft: (s) => svg(<path d="M19 12H5M11 18l-6-6 6-6" />, s),
  roundTrip: (s) => svg(<><path d="M17 2l4 4-4 4" /><path d="M3 11V9a3 3 0 0 1 3-3h15" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v2a3 3 0 0 1-3 3H3" /></>, s),
  flag: (s) => svg(<><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></>, s),
  plus: (s) => svg(<path d="M12 5v14M5 12h14" />, s, { strokeWidth: 2.6 }),
  minus: (s) => svg(<path d="M5 12h14" />, s, { strokeWidth: 2.6 }),
};

/* The page's top: back, what the page is, one line on what it is for, and Help. Light,
   so the first card starts right under it on a phone. */
export function TripHeader({ title, sub, onBack, backLabel, T }) {
  return (
    <div className="bg-gradient-to-b from-[#EAF4D3] to-[#F4F6F8]">
      <div className="max-w-lg lg:max-w-[1240px] mx-auto flex items-center gap-2.5 px-3 lg:px-6 pt-3 lg:pt-6 pb-2 lg:pb-4">
        <button type="button" onClick={onBack} aria-label={backLabel}
          className="w-10 h-10 rounded-full bg-white border border-[#E2E8F0] shadow-sm flex items-center justify-center shrink-0 text-[#1F2937] active:scale-95">
          {TripIcons.arrowLeft(18)}
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-[16px] lg:text-[24px] font-extrabold text-[#0F172A] leading-tight truncate">{title}</h1>
          {sub && <p className="text-[11px] lg:text-[14px] text-[#64748B] leading-snug mt-0.5 line-clamp-2">{sub}</p>}
        </div>
        <OnbHelpButton T={T} bordered />
      </div>
    </div>
  );
}

/* A white card with its own slim heading (mock Form_1, Oct 2026): a small icon and the
   title on one line, a line under it where there is one, and an optional control on the
   right (Detect location). The 36px icon box it had took a row of its own height. */
export function TripCard({ icon, title, sub, right, children, className = "" }) {
  return (
    <section className={`bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_10px_rgba(15,23,42,0.04)] p-3 ${className}`}>
      {title && (
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-start gap-1.5 min-w-0">
            <span className="text-[#5E8700] shrink-0 mt-[1px] flex">{icon}</span>
            <div className="min-w-0">
              <h2 className="text-[13.5px] font-bold text-[#0F172A] leading-tight">{title}</h2>
              {sub && <p className="text-[10.5px] text-[#64748B] leading-snug mt-0.5">{sub}</p>}
            </div>
          </div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export const FIELD_LABEL = "text-[11px] font-semibold text-[#4B5563] mb-1";

/* A place box: a pin inside on the left, and a × to empty it once something is in it. */
export function PlaceInput({ value, onChange, onBlur, placeholder, options, hasError, clearLabel, kind, flat = false, noClear = false }) {
  const filled = !!value?.value;
  const boxRef = useRef(null);
  /* A place said aloud (mock Four_1 change 9): the words go in the box, and the box is
     nudged as a key press would, so Google's list of places opens under it. */
  const spoken = (text) => {
    onChange({ value: text, selected: false });
    requestAnimationFrame(() => {
      const input = boxRef.current?.querySelector("input");
      if (!input) return;
      input.focus();
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  };
  // Flat: a row inside the route box - no border of its own, the pin in its circle, and
  // the right end kept clear for the box's swap button.
  return (
    <div className="relative" ref={boxRef}>
      <span className={`absolute ${flat ? "left-2.5" : "left-3"} top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none z-10 flex`}>
        {kind ? <PlaceMark kind={kind} size={flat ? 22 : 24} plain={!flat} /> : TripIcons.pin(15)}
      </span>
      <LocationAutocomplete
        hasError={hasError}
        className={flat
          ? `!h-[44px] !border-0 !rounded-none !bg-transparent !shadow-none !pl-[42px] !pr-[84px] text-[12px] font-medium`
          : "!h-[46px] !rounded-xl !pl-9 !pr-10 text-[12px] font-medium"}
        placeholder={placeholder} value={value} onChange={onChange} onBlur={onBlur} options={options} />
      {filled && !noClear && (
        <button type="button" aria-label={clearLabel} title={clearLabel}
          onClick={() => onChange({ value: "", selected: false })}
          className={`absolute ${flat ? "right-[44px]" : "right-1"} top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center text-[#94A3B8] text-[18px] leading-none z-10 active:scale-95`}>
          ×
        </button>
      )}
      {!filled && (
        <span className={`absolute ${flat ? "right-[46px]" : "right-1.5"} top-1/2 -translate-y-1/2 z-10 flex`}>
          <VoiceButton where={`place_${kind || "stop"}`} className="w-[32px] h-[32px]" size={15} onText={spoken} />
        </span>
      )}
    </div>
  );
}

/* "Same trip as on Drivers?" (mock Cross_1): the other page's trip, offered once when this
   form opens empty - its route and when, Yes to copy it, No for a new trip. */
export function SameTripCard({ title, route, when, yesLabel, noLabel, onYes, onNo }) {
  return (
    <section className="rounded-2xl border-[1.5px] border-[#7BAE00] bg-gradient-to-b from-[#F1F7E3] to-white px-3 py-2.5">
      <p className="text-[13px] font-extrabold text-[#0F172A]">{title}</p>
      <p className="text-[12px] font-bold text-[#1F2937] mt-1 truncate">{route}</p>
      {when && <p className="text-[11px] text-[#64748B] leading-snug">{when}</p>}
      <div className="flex gap-2 mt-2.5">
        <button type="button" onClick={onYes} className="h-[40px] px-4 rounded-xl bg-[#7BAE00] text-white text-[12px] font-extrabold active:scale-[0.98]">{yesLabel}</button>
        <button type="button" onClick={onNo} className="h-[40px] px-4 rounded-xl border border-[#CBD5E1] bg-white text-[#334155] text-[12px] font-bold active:scale-[0.98]">{noLabel}</button>
      </div>
    </section>
  );
}

/* The whole route in one box (mock Form_1, Oct 2026): pickup, any stops and the drop one
   under another with a hairline between, their pins joined by a dashed line, and the swap
   button on the right edge, on the line between the first two - where an empty row used
   to hold it. A row that is wrong turns the box's edge red. */
export function RouteBox({ children, onSwap, swapLabel, bad = false }) {
  const rows = Children.toArray(children).filter(Boolean);
  return (
    <div className={`relative rounded-xl border bg-white ${bad ? "border-[#DC3545]" : "border-[#E2E8F0]"}`}>
      {rows.length > 1 && (
        <span aria-hidden="true" className="absolute left-[20px] top-[33px] bottom-[33px] border-l-2 border-dashed border-[#CBD5E1] pointer-events-none" />
      )}
      {rows.map((row, i) => (
        <div key={i} className={i ? "border-t border-[#EEF2F7]" : ""}>{row}</div>
      ))}
      {onSwap && rows.length > 1 && (
        <button type="button" onClick={onSwap} aria-label={swapLabel} title={swapLabel}
          className="absolute right-2 top-[44px] -translate-y-1/2 z-20 w-9 h-9 rounded-full border border-[#D6E6B0] bg-white flex items-center justify-center text-[#5E8700] shadow-sm active:scale-95">
          {TripIcons.swap(15)}
        </button>
      )}
    </div>
  );
}

/* The pin, square or flag beside each place in the route, joined by a dashed line.
   `offset` brings the marker level with the middle of that place's box, below its label. */
export function RouteMarker({ kind, offset = 26 }) {
  const line = "w-0 border-l-2 border-dashed";
  return (
    <div className="w-5 shrink-0 flex flex-col items-center">
      <div className={`${line} shrink-0 ${kind === "pickup" ? "border-transparent" : "border-[#CBD5E1]"}`} style={{ height: offset }} />
      {kind === "pickup" && <span className="-my-1"><PlaceMark kind="pickup" size={22} /></span>}
      {kind === "stop" && <span className="w-2.5 h-2.5 rounded-sm bg-[#9CA3AF]" />}
      {kind === "drop" && <span className="-my-1"><PlaceMark kind="drop" size={22} /></span>}
      <div className={`${line} flex-1 ${kind === "drop" ? "border-transparent" : "border-[#CBD5E1]"}`} />
    </div>
  );
}

/* Pickup and drop change places. */
export function SwapButton({ onClick, label }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      className="w-9 h-9 rounded-full border border-[#D6E6B0] bg-white flex items-center justify-center text-[#5E8700] shadow-sm shrink-0 active:scale-95">
      {TripIcons.swap(15)}
    </button>
  );
}

/* "About 341 km · 6 hr 22 min" once both ends are known. */
export function RouteInfo({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F5F9] px-2.5 h-[30px] text-[10.5px] font-semibold text-[#334155] min-w-0">
      <span className="text-[#64748B] shrink-0">{TripIcons.car(14)}</span>
      <span className="truncate">{children}</span>
    </span>
  );
}

/* ── Dates and times ─────────────────────────────────────────────────────────── */

export function addDays(dateStr, n) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + n);
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// In English the phone's own way, as before; in the other languages, the app's.
export function prettyDate(dateStr, lang) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  const loc = !lang || lang === "en" ? undefined : dateLocale(lang);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString(loc, { weekday: "short", day: "numeric", month: "short" });
}

export function prettyTime(hhmm, lang) {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm || "";
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return !lang || lang === "en" ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : clockText(d, lang);
}

// Common times, one tap each. Any other time goes through the clock chip.
export const QUICK_TIMES = ["06:00", "08:00", "10:00", "14:00", "18:00", "21:00"];

const CHIP = "h-[34px] px-2.5 rounded-full border text-[11px] font-semibold whitespace-nowrap shrink-0 flex items-center gap-1 transition-all active:scale-[0.97]";
const chipTone = (on) => (on ? "bg-[#F1F7E3] border-[#7BAE00] text-[#3F5E00]" : "bg-white border-[#E5E7EB] text-[#374151]");

export function Chip({ on, disabled, onClick, children }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} className={`${CHIP} ${chipTone(on)} disabled:opacity-40`}>
      {children}
    </button>
  );
}

/* "→ Pickup *" - a green arrow going out, a red one coming back. */
export function WhenLabel({ kind, children }) {
  const out = kind === "pickup";
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#1F2937] shrink-0">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-white shrink-0 ${out ? "bg-[#7BAE00]" : "bg-[#DC3545]"}`}>
        {out ? TripIcons.arrowRight(12) : TripIcons.arrowLeft(12)}
      </span>
      {children} <span className="text-red-500 -ml-1">*</span>
    </span>
  );
}

/* The label on its own short line, then Today / Tomorrow (or Same day / Next day) and any
   other date through the phone's own calendar - an invisible date input laid over the
   chip - on one line (mock Form_1: "Other date" no longer drops to a line of its own). */
export function DateRow({ lead, value, options, min, max, onChange, otherLabel, bad }) {
  const { lang } = useLanguage();
  const isPreset = options.some((o) => o.value && o.value === value);
  const custom = !!value && !isPreset;
  return (
    <div className={bad ? "rounded-xl ring-1 ring-[#DC3545] p-1 -m-1" : ""}>
      {lead && <div className="mb-1.5">{lead}</div>}
      <div className="flex gap-1.5 items-center overflow-x-auto lg:overflow-visible lg:flex-wrap scrollbar-hide pb-0.5">
      {options.map((o) => (
        <Chip key={o.label} on={!!o.value && o.value === value} disabled={!o.value || (min && o.value < min) || (max && o.value > max)} onClick={() => onChange(o.value)}>
          {o.label}
        </Chip>
      ))}
      <label className={`${CHIP} ${chipTone(custom)} relative cursor-pointer`}>
        {TripIcons.calendar(13)}
        <span>{custom ? prettyDate(value, lang) : otherLabel}</span>
        <input type="date" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" value={value || ""} min={min || undefined} max={max || undefined}
          onClick={(e) => { try { e.target.showPicker(); } catch { /* the tap itself opens it */ } }}
          onChange={(e) => onChange(e.target.value)} />
      </label>
      </div>
    </div>
  );
}

/* The chosen time on a clock chip (tap for any time), then quick picks. A quick pick
   earlier than allowed (today, under 30 minutes from now) is greyed out. */
export function TimeRow({ value, min, onChange, inputRef, pickLabel, onBlur }) {
  const { lang } = useLanguage();
  return (
    <div className="flex gap-1.5 mt-1.5 overflow-x-auto lg:overflow-visible lg:flex-wrap scrollbar-hide pb-0.5">
      <label className={`${CHIP} ${chipTone(!!value)} relative cursor-pointer`}>
        {TripIcons.clock(13)}
        <span>{value ? prettyTime(value, lang) : pickLabel}</span>
        <input ref={inputRef} type="time" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" value={value || ""} min={min || undefined}
          onClick={(e) => { try { e.target.showPicker(); } catch { /* the tap itself opens it */ } }}
          onBlur={onBlur}
          onChange={(e) => onChange(e.target.value)} />
      </label>
      {QUICK_TIMES.map((q) => (
        <Chip key={q} on={value === q} disabled={!!min && q < min} onClick={() => onChange(q)}>{prettyTime(q, lang)}</Chip>
      ))}
    </div>
  );
}

/* A count with − and + (passengers, luggage). */
export function Stepper({ icon, label, sub, value, min, max, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="w-8 h-8 rounded-lg bg-[#F1F7E3] text-[#5E8700] flex items-center justify-center shrink-0">{icon}</span>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-[#1F2937] leading-tight">{label}</p>
          {sub && <p className="text-[10px] text-[#6B7280] leading-tight mt-0.5">{sub}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" aria-label={`${label} -`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}
          className="w-[36px] h-[36px] rounded-full border border-[#D1D5DB] bg-white flex items-center justify-center text-[#4B5563] disabled:opacity-40 active:scale-95">
          {TripIcons.minus(14)}
        </button>
        <span className="text-[15px] font-bold text-[#1F2937] w-7 text-center tabular-nums">{value}</span>
        <button type="button" aria-label={`${label} +`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}
          className="w-[36px] h-[36px] rounded-full bg-[#7BAE00] flex items-center justify-center text-white disabled:opacity-40 active:scale-95">
          {TripIcons.plus(14)}
        </button>
      </div>
    </div>
  );
}

/* The one button at the bottom, with what is still missing written above it. */
export function TripSubmitBar({ pending, onClick, disabled, children }) {
  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-[#E5E7EB] px-3 pt-2"
      style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" }}>
      <div className="max-w-lg mx-auto">
        {pending && (
          <p className="text-[10px] font-semibold text-[#6B7280] mb-1.5 px-0.5 truncate">{pending}</p>
        )}
        <button type="button" onClick={onClick} disabled={disabled}
          className="w-full h-[48px] rounded-xl text-[13px] font-bold flex items-center justify-center gap-2 shadow-md transition-all active:scale-[0.98] bg-[#7BAE00] hover:bg-[#6c9900] text-white disabled:bg-[#9CA3AF] disabled:shadow-none">
          {TripIcons.car(17)}
          <span className="truncate">{children}</span>
          {TripIcons.arrowRight(16)}
        </button>
      </div>
    </div>
  );
}

/* A laptop's trip form (mock Laptop_2, change 4): the trip so far on the right, staying
   in view, with the one button under it - in place of the phone's bar at the bottom.
   rows: [label, value] - a row with no value yet shows a dash. */
export function TripSummary({ title, rows, pending, ready, onClick, disabled, children }) {
  return (
    <aside className="hidden lg:block sticky top-[84px] bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(15,23,42,0.06)] p-5">
      <p className="text-[17px] font-extrabold text-[#0F172A]">{title}</p>
      <dl className="mt-2">
        {rows.filter(Boolean).map(([label, value]) => (
          <div key={label} className="flex items-start justify-between gap-3 py-2.5 border-b border-[#F1F5F9] last:border-0">
            <dt className="text-[13px] text-[#64748B] shrink-0">{label}</dt>
            <dd className={`text-[13.5px] font-bold text-right min-w-0 break-words ${value ? "text-[#0F172A]" : "text-[#CBD5E1]"}`}>{value || "—"}</dd>
          </div>
        ))}
      </dl>
      <button type="button" onClick={onClick} disabled={disabled}
        className="mt-4 w-full h-[50px] rounded-xl text-[15px] font-extrabold flex items-center justify-center gap-2 bg-[#7BAE00] hover:bg-[#6c9900] text-white transition-colors disabled:bg-[#9CA3AF]">
        {TripIcons.car(18)}<span className="truncate">{children}</span>{TripIcons.arrowRight(16)}
      </button>
      {pending
        ? <p className="mt-3 rounded-lg bg-[#FFF7ED] text-[#9A3412] text-[12.5px] font-semibold px-3 py-2 leading-snug">{pending}</p>
        : ready && <p className="mt-3 rounded-lg bg-[#F1F7E3] text-[#3F5E00] text-[12.5px] font-semibold px-3 py-2 leading-snug">✓ {ready}</p>}
    </aside>
  );
}
